import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  ThemeProvider,
  createTheme,
  Alert,
} from "@mui/material";
import Navbar from "../components/Navbar";
import { useSearchParams } from "react-router-dom";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

const CONTACT_WORKER_URL = "https://contact-sms.officialstartasite.workers.dev";

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: '#111111',
      paper: '#171717',
    },
    text: {
      primary: '#ffffff',
      secondary: '#aaaaaa',
    },
    primary: {
      main: '#c69733',
    },
    secondary: {
      main: '#e1bf77',
    },
  },
});

function ContactUs() {
  const [params] = useSearchParams()
  const service = params.get('service')

  const serviceMap = {
    maintenance: "Maintenance Detail",
    mini: "Mini Detail",
    "pre-sale": "Pre-Sale Detail",
  };
  

  const [formState, setFormState] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    selectedService: service && service in serviceMap ? serviceMap[service] : "No service",
    carDetails: "",
    message: "",
  });
  const [preferredDate, setPreferredDate] = useState(null);
  

  const [errors, setErrors] = useState({});
  const [alertMessage, setAlertMessage] = useState(null);
  const [disabled, setDisabled] = useState(false)

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormState({
      ...formState,
      [name]: value,
    });
    setErrors({})
  };

  const validate = () => {
    const newErrors = {};
    if (!formState.name) newErrors.name = "Name is required";
    if (!formState.email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formState.email)) {
      newErrors.email = "Email address is invalid";
    }
    if (!formState.phoneNumber)
      newErrors.phoneNumber = "Phone number is required";
    if (!formState.message) newErrors.message = "Message is required";
    return newErrors;
  };


  const handleSubmit = async (event) => {
    event.preventDefault();
    setDisabled(true)

    const formErrors = validate();

    if (Object.keys(formErrors).length === 0) {
      try {
        const res = await fetch(CONTACT_WORKER_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: formState.name,
            email: formState.email,
            phoneNumber: formState.phoneNumber,
            selectedService: formState.selectedService,
            carDetails: formState.carDetails,
            message: formState.message,
            preferredDate: preferredDate ? preferredDate.format("DD/MM/YYYY") : "",
          }),
        });

        if (!res.ok) throw new Error("Request failed");

        setAlertMessage({ type: 'success', message: "Your message has been received 🎉 We'll get back to you in less than 24 hours." });

      } catch (err) {
        console.error("Contact form submission failed:", err);
        setAlertMessage({ type: 'error', message: "We couldn't confirm your message went through. Please call or text us directly on 0416 614 496." });
        setDisabled(false)
      }
    } else {
      setErrors(formErrors);
      setDisabled(false)
    }
  };

  return (
    <ThemeProvider theme={darkTheme}>

    <div
      style={{
        background: "#111111",
        minHeight: '100vh',
        position: 'relative',
      }}
    >
      <div
        style={{
          maxHeight: '230px',
          position: 'absolute',
          width: '100%',
          marginTop: '65px',
          overflow: 'hidden',
        }}
      >
      <img src="images/Contact/IMG2.jpeg" style={{
        maxHeight: "230px",
        width: "100%",
        objectFit: "cover",
        filter: "brightness(0.68)",
      }} alt="car"/>
      </div>

      <div style={{ height: "60px", backgroundColor: "transparent" }}></div>
      <Navbar />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: '0 20px',
            paddingTop: '20px', 
            paddingBottom: '40px',
            backgroundColor: 'transparent',
            color: 'white'
          }}
        >
          <Typography
            variant="h2"
            align="center"
            sx={{
              color: "whitesmoke",
              zIndex: 900,
              marginTop: '40px',
              fontWeight: 'bold',
              textShadow: "0 8px 30px rgba(0,0,0,0.45)",
            }}
            gutterBottom
          >
            Contact us
          </Typography>
          <Typography sx={{ zIndex: 900, color: "rgba(255,255,255,0.84)" }}>
            Tell us about your car and we will get back within 24 hours.
          </Typography>
          <Typography sx={{ zIndex: 900, color: "rgba(255,255,255,0.6)", mt: '4px', fontSize: '14px' }}>
            Trouble reaching us? Call or text us directly on 0416 614 496.
          </Typography>
          <Box
            component="form"
            sx={{
              width: "100%",
              maxWidth: "580px",
              mt: '45px',
              p: { xs: 2, sm: 3 },
              borderRadius: "18px",
              background: "#171717",
              border: "1px solid rgba(198, 151, 51, 0.28)",
              boxShadow: "0 20px 44px rgba(0, 0, 0, 0.4)",
            }}
            onSubmit={handleSubmit}
          >
          {alertMessage && (
            <Alert
              severity={alertMessage.type === 'success' ? 'success' : 'error'}
              sx={{ marginBottom: '10px'}}
            >
              {alertMessage.message}
            </Alert>
          )}
            <TextField
              fullWidth
              label="Name"
              name="name"
              margin="normal"
              variant="outlined"
              required
              value={formState.name}
              onChange={handleChange}
              error={!!errors.name}
              helperText={errors.name}
              disabled={disabled}
            />

            <TextField
              fullWidth
              label="Email"
              name="email"
              margin="normal"
              variant="outlined"
              required
              value={formState.email}
              onChange={handleChange}
              error={!!errors.email}
              helperText={errors.email}
              disabled={disabled}
            />

            <TextField
              fullWidth
              label="Phone Number"
              name="phoneNumber"
              margin="normal"
              variant="outlined"
              required
              value={formState.phoneNumber}
              onChange={handleChange}
              error={!!errors.phoneNumber}
              disabled={disabled}
              helperText={errors.phoneNumber}
            />

            <FormControl fullWidth margin="normal" variant="outlined">
              <InputLabel>Service (optional)</InputLabel>
              <Select
                name="selectedService"
                value={formState.selectedService}
                onChange={handleChange}
                label="Service (optional)"
                disabled={disabled}
              >
                <MenuItem value="No service">N/A</MenuItem>
                <MenuItem value="Maintenance Detail">Maintenance Detail</MenuItem>
                <MenuItem value="Mini Detail">Mini Detail</MenuItem>
                <MenuItem value="Pre-Sale Detail">Pre-Sale Detail</MenuItem>

              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Vehicle Make & Model"
              name="carDetails"
              margin="normal"
              variant="outlined"
              value={formState.carDetails}
              onChange={handleChange}
              error={!!errors.carDetails}
              disabled={disabled}
              helperText={errors.carDetails}
            />

            <TextField
              fullWidth
              label="Message"
              name="message"
              margin="normal"
              variant="outlined"
              multiline
              rows={4}
              required
              value={formState.message}
              onChange={handleChange}
              disabled={disabled}
              error={!!errors.message}
              helperText={errors.message}
            />

            <Typography
              variant="body2"
              sx={{ mt: 2, color: "rgba(255,255,255,0.72)" }}
            >
              If this enquiry is about booking an appointment with us, please pick your preferred date below (optional)
            </Typography>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Preferred booking date (optional)"
                value={preferredDate}
                onChange={(newValue) => setPreferredDate(newValue)}
                disabled={disabled}
                disablePast
                sx={{ width: "100%", mt: 2, mb: 1 }}
              />
            </LocalizationProvider>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={disabled}
              color="primary"
              sx={{
                mt: 2,
                background: "#c69733",
                "&:hover": {
                  background: "#ad832b",
                },
                color: '#111111',
              }}
            >
              Submit
            </Button>
          </Box>
        </Box>
      </div>
    </ThemeProvider>
  );
}

export default ContactUs;
